import React, { useState, useEffect } from 'react';
import { consumeAPIs, IAPIPackage } from '../../commonUtils/APIContext';
import { NodeHierarchy } from './NodeHierarchy';
import { Tab, Button, Icon } from '@scuf/common';
import {
  IOrganisation,
  IRoleResponse,
  EModalOperations,
  INode
} from '../onboardingModels';
import { SplitPanelUtil } from '../../commonUtils/SplitPanelUtil';
import { useFetch, IUseFetch } from '../../commonUtils/useFetch';
import { AddUserDetails } from './AddUserDetails';
import { ViewUserDetails } from './ViewUserDetails';
import { checkEmailIsValid, checkEmailIsWithRestrictedDomainValid } from '../onboardingUtils';
import toasts from '../../commonUtils/toasts';
import { TreeNode } from '../../commonUtils/HierarchyTable';
import { flatten, get, intersection, union } from 'lodash';
import './UserModal.scss';
import i18next from '../../../i18next';
import { UpdateUserDetails } from './UpdateUserDetails';

interface IUserModalProps extends IAPIPackage {
  onClose: Function;
  onSave: Function;
  organisation: IOrganisation;
  mode: EModalOperations;
  panelSelectedRow?: any;
  isRoleEditable?: (user: any) => boolean;
  setDeleteModalOpen: (isOpen: boolean) => void;
}

interface UserAuth {
  roleId: string;
  nodeId: string;
  rootNodeId: string;
}

interface IUpdateRegisteredUser {
  roleId: string;
  email: string;
}

interface INewUser {
  email: string;
  authorizations: UserAuth[];
}

interface ISelectedNode {
  checked: boolean;
  partialChecked: boolean;
}

export const UserModal = (props: IUserModalProps) => {
  const [checkedRole, setCheckedRole] = useState<string>(props.panelSelectedRow ? props.panelSelectedRow?.roles.filter((role: any) => role.name === props.panelSelectedRow.role)[0]?.id : '');
  const [newUserEmailInput, setNewUserEmailInput] = useState<string>('');
  const [isUserEmailExist, setUserEmailExist] = useState<boolean>(false);
  const [modalMode, setModalMode] = useState<EModalOperations>(props.mode);
  const [privilegeName, setPrivilegeName] = useState<any>(null);
  const [activeIndex, setActiveIndex] = useState<number>(props.mode === EModalOperations.UPDATE ? 1 : 0);
  const [childNodeData, setChildNodeData] = useState([]);
  const [eidtSelectedNode, setEidtSelectedNode] = useState<TreeNode>();
  const [eidtSelectionType, setEidtSelectionType] = useState<string>();

  const [
    roleList,
    isLoadingRoleList,
    errorRoleList,
    getRoles
  ]: IUseFetch<IRoleResponse> = useFetch(
    `${props.baseURL}/api/v1/users/me/orgs/${props.organisation.id}/assignable-roles`,
    props.authFetch,
    undefined,
    modalMode === EModalOperations.UPDATE ? false : true
  );

  const [
    existingAuthorizations,
    isLoadingExistingAuthorizations,
    errorExistingAuthorizations,
    getExistingAuthorizations
  ]: IUseFetch<UserAuth[]> = useFetch(
    `${props.baseURL}/api/v1/users/${
      props.panelSelectedRow ? props.panelSelectedRow.id : undefined
    }/orgs/${props.organisation.id}/authorizations`,
    props.authFetch,
    undefined,
    modalMode === EModalOperations.UPDATE ? false : true
  );

  const [
    postNewUserResponse,
    isLoadingPostNewUser,
    errorPostNewUser,
    postNewUser
  ]: IUseFetch<IRoleResponse> = useFetch(
    `${props.baseURL}/api/v1/registered-users/orgs/${props.organisation.id}`,
    props.authFetch,
    undefined,
    true
  );

  useEffect(() => {
    if (
      !isLoadingPostNewUser &&
      errorPostNewUser === undefined &&
      postNewUserResponse !== null
    ) {
      props.onClose(false);
      props.onSave();
      toasts.success(i18next.t('addUserSuccess'));
    } else {
      if (errorPostNewUser) {
        if (errorPostNewUser.code === 409) {
          if (activeIndex > 0) {
            setActiveIndex(0);
          }
          setUserEmailExist(true);
          toasts.error(i18next.t('userAlreadyExist'));
        } else {
          toasts.error(i18next.t('addUserError'));
        }
      }
    }
  }, [isLoadingPostNewUser]);

  const [
    editUserResponse,
    isLoadingEditUser,
    errorEditUser,
    editUser
  ]: IUseFetch<IRoleResponse> = useFetch(
    `${props.baseURL}/api/v1/users/${
      props.panelSelectedRow ? props.panelSelectedRow.id : undefined
    }/orgs/${props.organisation.id}/authorizations`,
    props.authFetch,
    undefined,
    true
  );

  useEffect(() => {
    if (
      !isLoadingEditUser &&
      errorEditUser === undefined &&
      editUserResponse !== null
    ) {
      props.onClose(false);
      props.onSave();
      toasts.success(i18next.t('editUserSuccess'));
    } else {
      if (errorEditUser) {
        toasts.error(i18next.t('editUserError'));
      }
    }
  }, [isLoadingEditUser]);

  // add editRegisteredUserFetch as above
  const [
    registeredUserUpdateResponse,
    isLoadingRegisteredUserUpdate,
    errorRegisteredUserUpdate,
    editRegisteredUser
  ]: IUseFetch<boolean> = useFetch(
    `${props.baseURL}/api/v1/registered-users/orgs/${props.organisation.id}`,
    props.authFetch,
    undefined,
    true
  );

  useEffect(() => {
    if (
      !isLoadingRegisteredUserUpdate &&
      errorRegisteredUserUpdate === undefined &&
      registeredUserUpdateResponse !== null
    ) {
      props.onClose(false);
      props.onSave();
      toasts.success(i18next.t('editUserSuccess'));
    } else if (errorRegisteredUserUpdate) {
      toasts.error(i18next.t('editUserError'));
    }
  }, [isLoadingRegisteredUserUpdate]);

  useEffect(() => {
    if (
      !isLoadingRoleList &&
      !errorRoleList &&
      roleList !== null &&
      roleList.resources.length > 0
    ) {
      if(modalMode !== EModalOperations.UPDATE){
        setCheckedRole(roleList.resources[0].id);
      }
    }
  }, [isLoadingRoleList, errorRoleList]);

  const formNewUserObject = () => {
    const authorizations: UserAuth[] = [];
    let selectedNodes = listOfSelectedNodes();

    let n = getNodes(selectedNodes, organisationHierarchy[0], []);
    n.forEach(item => {
      const node = hierarchyMap.get(item);
      if (node && node.selectable) {
        authorizations.push({
          roleId: checkedRole,
          nodeId: item,
          rootNodeId: node.data.rootNodeId
        });
      }
    });

    const newStagedUser: INewUser = {
      email: newUserEmailInput,
      authorizations
    };
    return newStagedUser;
  };

  const getNodes = (
    selectedNodes: string[],
    node: TreeNode,
    list: string[]
  ): string[] => {
    if (selectedNodes.includes(node.key)) {
      list.push(node.key);
      return list;
    }

    list = flatten(
      node.children.map(child => getNodes(selectedNodes, child, []))
    );

    return list;
  };

  const sendPostNewUser = () => {
    const newUser: INewUser = formNewUserObject();
    if(checkEmailIsWithRestrictedDomainValid(newUser.email))
    {
      const newUserPostObject = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newUser)
      };
      postNewUser(newUserPostObject);
    }
    else{
      toasts.error(i18next.t('restrictedDomain'));
    }
  };

  const formEditUserObject = () => {
    const authorizations: UserAuth[] = [];
    var roleObj: any;    
    if(modalMode === EModalOperations.UPDATE && checkedRole !== '') {
      roleObj = roleList.resources.find(
        (roleObj: any) => roleObj.id === checkedRole
      );
    } else{
      roleObj = props.panelSelectedRow.roles.find(
        (roleObj: any) => roleObj.name === props.panelSelectedRow.role
      );
    }   

    if (roleObj) {
      if(!isNodeUpdated() && isRoleUpdated()){        
        existingAuthorizations?.forEach((auth: UserAuth) => {
          authorizations.push({
            roleId: checkedRole,
            nodeId: auth.nodeId,
            rootNodeId: auth.rootNodeId
          });
        });      
      } else {
        let selectedNodes = listOfSelectedNodes();
        let n = getNodes(selectedNodes, organisationHierarchy[0], []);
        n.forEach(item => {
          const node = hierarchyMap.get(item);
          if (node && node.selectable) {
            authorizations.push({
              roleId: roleObj.id,
              nodeId: item,
              rootNodeId: node.data.rootNodeId
            });
          }
        });
      }

    }
    return authorizations;
  };

  const listOfSelectedNodes = () => {
    let list: string[] = [];
    for (let [key, value] of Object.entries<ISelectedNode>(selectedNode)) {
      if (value.checked) {
        list.push(key);
      }
    }
    return list;
  };

  const isAnyNodeSelected = () => {
    let list: string[] = [];
    for (let [key, value] of Object.entries<ISelectedNode>(selectedNode)) {
      const node = hierarchyMap.get(key);
      if (value.checked && node && node.selectable) {
        list.push(key);
      }
    }
    return list.length > 0;
  };

  const editExistingUser = () => {

    if(props.panelSelectedRow.registrationStatus.type !== 'Registered'){
      const roleObj = roleList.resources.find(
        (roleObj: any) => roleObj.id === checkedRole
      );
      if (roleObj) {
        const editRegisteredUserObject = {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            role: roleObj.id,
            email: props.panelSelectedRow.email
          })
        };
        editRegisteredUser(editRegisteredUserObject);
      }
      return;
    }

    const authorizations: UserAuth[] = formEditUserObject();
    const editUserObject = {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(authorizations)
    };
    editUser(editUserObject);
  };

  /************************Handling Node Hierarchy start ***********************************/

  const [organisationHierarchy, setOrganisationHierarchy] = useState<
    TreeNode[]
  >([]);
  const [hierarchyMap, setHierarchyMap] = useState(new Map<string, TreeNode>());

  const [organisationDataLoading, setOrganisationDataLoading] = useState<
    boolean
  >(false);

  const [selectedNode, setSelectedNode] = useState([]);
  const [assignedNodes, setAssignedNodes] = useState<string[]>([]);
  const [itemSelectedNode, setItemSelectedNode] = useState([]);

  const treeToArray = (
    tree: any,
    parentId: string | null | undefined = null
  ) => {
    const array: TreeNode[] = [];
    tree.forEach((item: TreeNode) => {
      const node = { ...item };
      node.parentId = parentId as string;
      array.push(node);
      if (item.children) {
        const children = treeToArray(item.children, item.key);
        array.push(...children);
      }
    });
    return array;
  };

  const findParentHierarchy = (
    array: TreeNode[],
    key: string,
    hierarchy: TreeNode[] = []
  ): any => {
    for (let item of array) {
      if (item.key === key) {
        return [...hierarchy, item];
      } else if (item.children) {
        let parentHierarchy = findParentHierarchy(item.children, key, [
          ...hierarchy,
          item
        ]);
        if (parentHierarchy) {
          return parentHierarchy;
        }
      }
    }
    return null;
  };

  const arrayToTree = (
    array: TreeNode[],
    parentId: string | null | undefined = null
  ): any => {
    const tree: TreeNode[] = [];
    array.forEach((item: TreeNode) => {
      if (item.parentId === parentId) {
        const children = arrayToTree(array, item.key);
        item.children = children;
        tree.push(item);
      }
    });
    return tree;
  };

  const updateOrgSelection = (data: TreeNode, type: string) => {
     setEidtSelectedNode(data);
     setEidtSelectionType(type);

    let hierarchy = findParentHierarchy(organisationHierarchy, data.key);
    hierarchy = hierarchy.reverse();
    hierarchy.forEach((obj: TreeNode) => {
      if (data.key === obj.key) {
        obj.itemChecked = type === 'add' ? true : false;
        obj.itemPartialChecked = false;
        if (obj.children.length > 0) {
          let childHierarchyItems = treeToArray(obj.children);
          childHierarchyItems.forEach((childItems: TreeNode) => {
            childItems.itemChecked = type === 'add' ? true : false;
            childItems.itemPartialChecked = false;
          });
          let changeTreeFormat = arrayToTree(childHierarchyItems);
          obj.children = changeTreeFormat;
        }
      }
      if (obj.children.length > 0) {
        let findItemChecked = obj.children.filter(
          (o: TreeNode) => o.itemChecked === true
        );
        let findItemPartialChecked = obj.children.filter(
          (o: TreeNode) => o.itemPartialChecked === true
        );
        if (findItemChecked.length === obj.children.length) {
          obj.itemChecked = true;
          obj.itemPartialChecked = false;
        } else if (
          (findItemChecked.length > 0 &&
            findItemChecked.length < obj.children.length) ||
          findItemPartialChecked.length > 0
        ) {
          obj.itemChecked = false;
          obj.itemPartialChecked = true;
        } else if (
          findItemChecked.length === 0 &&
          findItemPartialChecked.length === 0
        ) {
          obj.itemChecked = false;
          obj.itemPartialChecked = false;
        }
      }
    });
    updateSelectionValue();
  };

  const updateSelectionValue = () => {
    let selectionItems: any = {};
    let findSelectedAllItems = treeToArray(organisationHierarchy);
    findSelectedAllItems.forEach((item: any) => {
      selectionItems[item.key] = {
        checked: item.itemChecked,
        partialChecked: item.itemPartialChecked
      };
    });
    setItemSelectedNode(selectionItems);
  };

  useEffect(() => {
    if (props.mode === EModalOperations.READ || props.mode === EModalOperations.UPDATE) {
      props
        .authFetch(
          `${props.baseURL}/api/v1/users/orgs/${props.organisation.id}/nodes?targetResourceId=${props.panelSelectedRow.id}`
        )
        .then(res => res.json() as Promise<string[]>)
        .then(assignedNodesList => {
          setSelectedNode(createSelectedNodeObj(assignedNodesList));
          setAssignedNodes(assignedNodesList);
        })
        .catch(e => {
          console.error(`${i18next.t('nodesError')}: ${e}`);
          toasts.error(i18next.t('nodesError'));
        })
        .finally(() => {
          setOrganisationDataLoading(false);
        });
    } else if (props.mode === EModalOperations.CREATE) {
      getRoles();
    }
  }, [props.organisation]);

  useEffect(() => {
    if (assignedNodes.length > 0) {
      if (modalMode === EModalOperations.READ) {
        hierarchyMap.forEach(function(node: TreeNode, key: string) {
          node.className = assignedNodes.includes(key) ? 'custom-selected' : '';
          node.selectable = false;
        });
      } else if (modalMode === EModalOperations.UPDATE) {
        setActiveIndex(1);
        hierarchyMap.forEach(function(node: TreeNode, key: string) {
          node.className = '';
          node.selectable = node.data.hasWritePrivilege && isActiveUser();
        });
      }
      setOrganisationHierarchy([...organisationHierarchy]);
    }
  }, [modalMode, assignedNodes]);

  const findPrivilegeString = () => {
    let privilege = null;
    if (modalMode === EModalOperations.CREATE) {
      if (roleList !== null && roleList.resources.length > 0) {
        const role = roleList.resources.find(res => res.id === checkedRole);
        if (role && role.name && role.name !== null) {
          privilege = `UserManagement_UserRoles_Assign_${role.name
            .trim()
            .split(' ')
            .join('')}`;
          setPrivilegeName(privilege);
        }
      }
    } else {
      if (props.panelSelectedRow) {
        privilege = `UserManagement_UserRoles_Assign_${props.panelSelectedRow.role
          .trim()
          .split(' ')
          .join('')}`;
        setPrivilegeName(privilege);
      }
    }
    return privilege;
  };

  const createSelectedNodeObj = (assignedNodesList: string[]) => {
    let obj: any = {};
    assignedNodesList.forEach(function(key: string) {
      obj[key] = {
        checked: true,
        partialChecked: false
      };
    });
    return obj;
  };

  useEffect(() => {
    setSelectedNode([]); 
    fetchRootHierarchy();   
  }, [props.organisation]);

  useEffect(() => {
    if(modalMode !== EModalOperations.UPDATE){
      fetchRootHierarchy();
    }    
  }, [checkedRole]);

  const isActiveUser = () => {
    if(modalMode !== EModalOperations.UPDATE){
      return true;
    }
    return props.panelSelectedRow?.registrationStatus?.type === 'Registered' ? true : false; 
  }

  const fetchRootHierarchy = () => {
    const privilege = findPrivilegeString();
    if (privilege !== null) {
      setOrganisationDataLoading(true);
      props
        .authFetch(
          `${props.baseURL}/api/v1/users/orgs/${props.organisation.id}/nodes/${props.organisation.id}/hierarchy?privileges=${privilege}&fetch=current`
        )
        .then(res => res.json() as Promise<INode[]>)
        .then(nodes => {
          const orgHierarchyStructure: TreeNode[] = nodes.map(org => {
            const hasWritePrivilege =
              org.privileges.length > 0 && org.privileges.includes(privilege);
            const treeNode: TreeNode = {
              key: org.id,
              children: [],
              leaf: org.childCount <= 0,
              className: `${
                modalMode === EModalOperations.READ &&
                assignedNodes.length > 0 &&
                assignedNodes.includes(org.id)
                  ? 'custom-selected'
                  : ''
              }`,
              selectable:
                modalMode !== EModalOperations.READ && hasWritePrivilege && isActiveUser(),
              data: { ...org, rootNodeId: org.id, hasWritePrivilege },
              itemChecked: false,
              itemPartialChecked: false,
              rootNode: true
            };
            hierarchyMap.set(org.id, treeNode);
            return treeNode;
          });
          setOrganisationHierarchy(orgHierarchyStructure);
        })
        .catch(e => {
          console.error(`${i18next.t('hierarchyError')}: ${e}`);
          toasts.error(i18next.t('hierarchyError'));
        })
        .finally(() => {
          setOrganisationDataLoading(false);
        });
    }
  }

  const fetchChildrenAndInsert = (rootNodeId: string, parentId: string) => {
    // Block ui and show loading icon only when data is not present,
    // but still make call and update if there is some data there
    if (privilegeName !== null) {
      setOrganisationDataLoading(true);
      props
        .authFetch(
          `${props.baseURL}/api/v1/users/orgs/${rootNodeId}/nodes/${parentId}/hierarchy?privileges=${privilegeName}&fetch=immediate`
        )
        .then(res => res.json() as Promise<INode[]>)
        .then(childrenNodeResponse => {
          let selectedNodes = listOfSelectedNodes();
          let immediateParentId = '';
          if (childrenNodeResponse.length > 0) {
            immediateParentId = childrenNodeResponse[0].parentId;
          }
          let obj: any = {};
          const isImmediateParentSelected = selectedNodes.includes(
            immediateParentId
          );
          const childrenNodes: TreeNode[] = childrenNodeResponse.map(node => {
            const hasWritePrivilege =
              node.privileges.length > 0 &&
              node.privileges.includes(privilegeName);
            const treeNode: TreeNode = {
              key: node.id,
              children: [],
              leaf: node.childCount === 0,
              itemChecked: hasWritePrivilege && selectedNodes.includes(node.id),
              className: `${
                modalMode === EModalOperations.READ &&
                assignedNodes.length > 0 &&
                assignedNodes.includes(node.id)
                  ? 'custom-selected'
                  : ''
              }`,
              selectable:
                modalMode !== EModalOperations.READ && hasWritePrivilege && isActiveUser(),
              data: { ...node, rootNodeId: rootNodeId, hasWritePrivilege },
              itemPartialChecked: false,
              rootNode: false
            };
            hierarchyMap.set(node.id, treeNode);
            if (
              modalMode !== EModalOperations.READ &&
              hasWritePrivilege &&
              isImmediateParentSelected &&
              !selectedNodes.includes(node.id)
            ) {
              obj[node.id] = {
                checked: true,
                partialChecked: false
              };
            }
            return treeNode;
          });

          if (Object.entries<ISelectedNode>(obj).length > 0) {
            let newVar = { ...selectedNode, ...obj };
            setSelectedNode(newVar);
          }
          addToHierarchy(parentId, childrenNodes);
          let temp: any = [];
          childrenNodes.forEach((item: any) => {
            if (item.itemChecked) {
              temp.push(item);
            }
          });

          setChildNodeData(temp);
        })
        .catch(e => {
          console.error(`${i18next.t('childNodesError')}: ${e}`);
          toasts.error(i18next.t('childNodesError'));
        })
        .finally(() => {
          setOrganisationDataLoading(false);
          updateSelectionValue();
        });
    }
  };

  useEffect(() => {
    if (childNodeData.length > 0) {
      childNodeData.forEach(item => {
        updateOrgSelection(item, 'add');
      });
    }
  }, [childNodeData]);

  const addToHierarchy = (targetId: string, children: Array<TreeNode>) => {
    if (organisationHierarchy) {
      setOrganisationHierarchy(prevState =>
        prevState.map(node => putChildrenInHierarchy(targetId, node, children))
      );
    }
  };

  const putChildrenInHierarchy = (
    targetId: string,
    hierarchyNode: TreeNode,
    children: Array<TreeNode>
  ) => {
    if (hierarchyNode.key === targetId) {
      // Check children arent different at immediate level
      const childCheck = children.map(targetChild =>
        hierarchyNode.children.find(
          sourceChild => sourceChild.key === targetChild.key
        )
      );

      if (childCheck.includes(undefined)) {
        hierarchyNode.children = children;
      }
    } else if (hierarchyNode.children) {
      hierarchyNode.children = hierarchyNode.children.map(child =>
        putChildrenInHierarchy(targetId, child, children)
      );
    }
    return hierarchyNode;
  };

  const onExpandRow = (rowExpanding: {
    originalEvent: Event;
    node: TreeNode;
  }): void => {
    if (rowExpanding.node.children.length === 0) {
      fetchChildrenAndInsert(
        rowExpanding.node.data.rootNodeId,
        rowExpanding.node.key
      );
    }
  };

  const isRoleUpdated = (): boolean => {
    if (modalMode === EModalOperations.UPDATE) {
      return checkedRole !== props.panelSelectedRow?.roles.filter((role: any) => role.name === props.panelSelectedRow.role)[0]?.id;
    }
    return false;
  }

  /************************Handling Node Hierarchy end ***********************************/

  const handleChange = (value: any, type: string) => {
    if (type === 'email') {
      setNewUserEmailInput(value);
      if (isUserEmailExist) {
        setUserEmailExist(false);
      }
    } else if (type === 'role') {
      setCheckedRole(value);
    } else if (type === 'node') {
      setSelectedNode(value);
    }
  };

  const getHeader = () => {
    if (modalMode === EModalOperations.CREATE) {
      return `${i18next.t('addUser')}`;
    } else if (modalMode === EModalOperations.READ) {
      return (
        <div className="view-user-header">
          {props.panelSelectedRow && props.panelSelectedRow.email ? (
            <>
              <span>{props.panelSelectedRow.email}</span>
              {props.panelSelectedRow.status === 'Registered' && (
                <Icon
                  className="large-space"
                  root="common"
                  name="edit"
                  size="small"
                  onClick={() => {
                    setModalMode(EModalOperations.UPDATE);
                    getExistingAuthorizations();
                    getRoles();
                  }}
                />
              )}
            </>
          ) : (
            <span>{i18next.t('noUserSelected')}</span>
          )}
        </div>
      );
    } else if (modalMode === EModalOperations.UPDATE) {
      return `${i18next.t('editUser')}`;
    } else {
      return '';
    }
  };

  const isNodeUpdated = (): boolean => {
    const checkedNodes: string[] = Object.entries<ISelectedNode>(selectedNode)
      .filter(([key, value]) => value.checked && hierarchyMap.get(key)?.selectable)
      .map(([key]) => key);
  
    const intersectingNodes = intersection(checkedNodes, assignedNodes);
    const unionNodes = union(checkedNodes, assignedNodes);
  
    // Check if editing a selected node
    if (eidtSelectedNode !== undefined && eidtSelectedNode.children.length > 0) {
      // Check if removing a child node
      if (eidtSelectionType === 'remove') {
        const childHierarchyItems = treeToArray(eidtSelectedNode.children);
        const shouldUpdate = childHierarchyItems.some(
          (childItem: TreeNode) =>
            !childItem.itemChecked && assignedNodes.includes(childItem?.key)
        );
        if (shouldUpdate) {
          return true;
        }
      }
    } else if (eidtSelectionType === 'remove' && assignedNodes.includes(eidtSelectedNode?.key)) {
      return true;
    }
  
    // Check if there are no selected nodes or child nodes and no intersection between selected and assigned nodes
    if (!checkedNodes.length && !childNodeData.length && !intersectingNodes.length) {
      return false;
    }
  
    // Check if all assigned nodes are selected and if any child nodes are unselected
    if (unionNodes.length && assignedNodes.length === unionNodes.length) {
      const hasUnselectedChildNodes = childNodeData.some(
        (item: any) => !item.itemChecked && assignedNodes.includes(item.key)
      );
      return hasUnselectedChildNodes;
    }
  
    // Check if the number of assigned nodes is different from the number of intersecting and union nodes
    return assignedNodes.length !== intersectingNodes.length || assignedNodes.length !== unionNodes.length;
  };
  
  const cancelEditModal = () => {
    setModalMode(EModalOperations.READ);
    setSelectedNode(createSelectedNodeObj(assignedNodes));
  };

  return (
    <div className="user-modal">
    <SplitPanelUtil.Panel onOverlayClicked={() => props.onClose(false)}>
      <SplitPanelUtil.PanelHeader onPanelClose={() => props.onClose(false)}>
        <SplitPanelUtil.PanelHeaderTitle>
          {getHeader()}
        </SplitPanelUtil.PanelHeaderTitle>
      </SplitPanelUtil.PanelHeader>
      <SplitPanelUtil.PanelContent>
        <Tab
          activeIndex={activeIndex}
          onTabChange={(activeIndex: number) => setActiveIndex(activeIndex)}
        >
          <Tab.Pane title={i18next.t('details')}>
            <SplitPanelUtil.PanelContentMain>
              <SplitPanelUtil.PanelContentMainSection>
                {modalMode === EModalOperations.CREATE ? (
                  <AddUserDetails
                    roleList={roleList}
                    isLoadingRoleList={isLoadingRoleList}
                    errorRoleList={errorRoleList}
                    newUserEmailInput={newUserEmailInput}
                    isUserEmailExist={isUserEmailExist}
                    role={checkedRole}  
                    handleChange={handleChange}
                  />
                ) : modalMode === EModalOperations.READ ? (
                  <ViewUserDetails panelSelectedRow={props.panelSelectedRow} />
                ) : modalMode === EModalOperations.UPDATE ? 
                <UpdateUserDetails 
                    panelSelectedRow={
                      props.panelSelectedRow
                    } 
                    roleList={roleList}
                    errorRoleList={errorRoleList}
                    isLoadingRoleList={isLoadingRoleList}
                    role={checkedRole}
                    isRoleEditable={props.isRoleEditable}
                    handleChange={handleChange}
                />
                  : null}
              </SplitPanelUtil.PanelContentMainSection>
            </SplitPanelUtil.PanelContentMain>
          </Tab.Pane>
          <Tab.Pane title={i18next.t('nodes')}>
            <SplitPanelUtil.PanelContentMain className="user-modal-content">
              <SplitPanelUtil.PanelContentMainSection>
                <NodeHierarchy
                  organisationHierarchy={organisationHierarchy}
                  loading={organisationDataLoading}
                  handleChange={handleChange}
                  selectedNode={itemSelectedNode}
                  onExpand={onExpandRow}
                  onSelect={data => updateOrgSelection(data, 'add')}
                  onUnselect={data => updateOrgSelection(data, 'remove')}
                />
              </SplitPanelUtil.PanelContentMainSection>
            </SplitPanelUtil.PanelContentMain>
          </Tab.Pane>
        </Tab>
      </SplitPanelUtil.PanelContent>
      <SplitPanelUtil.PanelFooter>
        {modalMode !== EModalOperations.READ && (
          <>
            <div>
              { modalMode === EModalOperations.UPDATE && 
              (props.isRoleEditable ? props.isRoleEditable(props.panelSelectedRow) : false) && (
                <Button
                  type="secondary"
                  size="medium"
                  content={i18next.t('delete')}
                  onClick={() => {
                    props.setDeleteModalOpen(true);
                  }}
                />
              )}
            </div>
            <div>
              <Button
                type="secondary"
                size="medium"
                content={i18next.t('cancel')}
                onClick={() =>
                  modalMode === EModalOperations.UPDATE
                    ? cancelEditModal()
                    : props.onClose(false)
                }
              />
              {modalMode === EModalOperations.CREATE && (
                <Button
                  type="primary"
                  size="medium"
                  content={i18next.t('submit')}
                  loading={isLoadingPostNewUser}
                  disabled={
                    !(
                      !isLoadingPostNewUser &&
                      checkEmailIsValid(newUserEmailInput) &&
                      checkedRole !== '' &&
                      isAnyNodeSelected()
                    )
                  }
                  onClick={() => sendPostNewUser()}
                />
              )}
              {modalMode === EModalOperations.UPDATE && (
                <Button
                  type="primary"
                  size="medium"
                  content={i18next.t('submit')}
                  loading={isLoadingEditUser}
                  disabled={!(!isLoadingEditUser && !isLoadingExistingAuthorizations && (isNodeUpdated() || isRoleUpdated()))}
                  onClick={() => editExistingUser()}
                />
              )}
            </div>
          </>
        )}
      </SplitPanelUtil.PanelFooter>
    </SplitPanelUtil.Panel>
  </div>
  );
};

export default consumeAPIs(UserModal);
